import { IClockinResponse } from '$models'
import { timeClockinService } from '$services'
import { enqueueLoadedSnackbar, exceptionMessageHandler, logger } from '$utils'

export interface SaveClockinOptions {
  /** The clockin with the edits applied */
  clockin: IClockinResponse
  getClockins: () => IClockinResponse[]
  /** Not needed when refetchAfterSave is given, since nothing is patched then */
  setClockins?: (clockins: IClockinResponse[]) => void
  setLoading: (isLoading: boolean) => void
  successMessage: string
  errorMessage: string
  /** Runs instead of patching the list when the save can change other rows too */
  refetchAfterSave?: () => Promise<unknown>
}

/**
 * Saves an edited clockin and puts the result back in the list.
 * @returns true when the save went through
 */
export const saveClockin = async ({
  clockin,
  getClockins,
  setClockins,
  setLoading,
  successMessage,
  errorMessage,
  refetchAfterSave,
}: SaveClockinOptions): Promise<boolean> => {
  const isInTheList = getClockins().some(
    (existing) => existing.id === clockin.id
  )

  if (!isInTheList) {
    enqueueLoadedSnackbar('Failed to find the clockin entry to update', 'error')
    return false
  }

  // The API rejects a null hourly rate, so drop it before sending
  const payload = { ...clockin }
  if (payload.hourlyRate == null) {
    delete payload.hourlyRate
  }

  try {
    setLoading(true)

    const updatedClockin = await timeClockinService.update(payload)

    if (!updatedClockin) {
      logger.error(`${errorMessage} - the API returned no clockin`)
      enqueueLoadedSnackbar(errorMessage, 'error')
      return false
    }

    if (refetchAfterSave) {
      await refetchAfterSave()
    } else {
      setClockins?.(
        getClockins().map((existing) =>
          existing.id === updatedClockin.id ? updatedClockin : existing
        )
      )
    }

    enqueueLoadedSnackbar(successMessage, 'success')
    return true
  } catch (error) {
    exceptionMessageHandler(error, errorMessage)
    return false
  } finally {
    setLoading(false)
  }
}
